// Single source of truth for the Event document shape.
// Modules add themselves via the registry; their config slice lives in
// `event.modules[<moduleId>]`. Disabling a module is just `enabled: false`.

export type ImageVariant = {
  url: string;
  width: number;
  /** Pixel height of the encoded variant. Optional for backward compat:
   *  banner manifests written before this field was plumbed through don't
   *  carry height. New uploads populate it from the canvas dimensions. */
  height?: number;
  format: "webp" | "jpeg" | "avif" | "png";
};

export type ImageAsset = {
  alt: string;
  /** Public URL of the canonical image used as <img src>. */
  src: string;
  /** Optional optimized variants populated by the post-upload Worker. */
  variants?: ImageVariant[];
  /** Optional dominant color for placeholder. */
  blurColor?: string;
};

export type CTA = {
  label: string;
  to: string;
  variant?: "primary" | "outline" | "subtle";
  icon?: "rsvp" | "potluck" | "competition" | "donate" | "junior";
};

export type ModuleId =
  | "hero"
  | "agenda"
  | "location"
  | "rsvp"
  | "potluck"
  | "competition"
  | "junior"
  | "donation";

export type BaseModuleConfig = {
  enabled: boolean;
  navLabel?: string;
};

export type HeroConfig = BaseModuleConfig & {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  date: string;
  location?: string;
  banner?: ImageAsset;
  ctas?: CTA[];
};

export type AgendaItem = {
  start: string;
  end?: string;
  title: string;
  description?: string;
  icon?: "prayer" | "speech" | "meal" | "poem" | "game" | "award" | "kids" | "music";
  highlight?: boolean;
};

export type AgendaConfig = BaseModuleConfig & {
  title?: string;
  dateLabel?: string;
  items: AgendaItem[];
};

export type LocationConfig = BaseModuleConfig & {
  title?: string;
  /**
   * Free-form multi-line address shown above the map. Optional because
   * some events may want the map alone; the public renderer guards
   * the empty case.
   */
  address?: string;
  lat: number;
  lng: number;
  zoom?: number;
  showDirections?: boolean;
};

export type RsvpConfig = BaseModuleConfig & {
  title: string;
  description?: string;
  ctaLabel?: string;
  capacity?: number;
  /**
   * Age ranges offered, one dropdown per child, so organizers can plan
   * dedicated programs. Stored in the document so it can be tuned per event.
   * An empty array is treated the same as absent. The fallback to
   * {@link DEFAULT_CHILD_AGE_GROUPS} is applied by each consumer (the public
   * RSVP form and the RSVP API endpoints), not by the schema itself, so code
   * reading this field directly must apply the same fallback.
   */
  childAgeGroups?: string[];
};

/** Default child age ranges shown when an event does not define its own. */
export const DEFAULT_CHILD_AGE_GROUPS: readonly string[] = [
  "0-2",
  "3-6",
  "7-10",
  "11-13",
  "14-17",
];

export type PotluckCategory = {
  id: string;
  label: string;
  emoji?: string;
};

export type PotluckConfig = BaseModuleConfig & {
  title: string;
  description?: string;
  categories: PotluckCategory[];
  /**
   * Show the running list of contributors and dishes on the public page.
   * Defaults to true; admins can disable for events where surprise is part
   * of the experience.
   */
  showSubmissions?: boolean;
  /** Optional inline notice (e.g. halal requirements). Rendered as a callout. */
  notice?: string;
};

/**
 * Generic community competition. The same module powers a cake bake-off,
 * an art show, a Quran recitation contest, a poetry slam, etc. The admin
 * configures the vocabulary ("entryNoun" = "cake" / "drawing" / ...).
 */
export type CompetitionConfig = BaseModuleConfig & {
  title: string;
  description?: string;
  /** Singular noun for a competing entry: "cake", "drawing", "dish". */
  entryNoun: string;
  /** Singular noun for the participant: "baker", "artist", "reciter". */
  participantNoun: string;
  /** Optional kind for visual flourishes ("cake" emoji etc.). Free-form. */
  kind?: string;
  judgingMode: "ranking" | "score";
  /** Fields shown on the entry form, in order. */
  fields: Array<{
    id: string;
    label: string;
    type: "text" | "longtext" | "boolean";
    required?: boolean;
    placeholder?: string;
    hint?: string;
    /** If true, this field's value is shown on the public entries list. */
    publiclyVisible?: boolean;
  }>;
  /**
   * Show the list of registered entries on the public page so visitors can
   * see who's competing. Off by default; admins opt in.
   */
  showEntries?: boolean;
};

export type JuniorConfig = BaseModuleConfig & {
  title: string;
  description?: string;
  ageGroups: Array<{ label: string; description?: string }>;
  stations?: Array<{ name: string; description?: string }>;
};

export type DonationConfig = BaseModuleConfig & {
  title: string;
  description?: string;
  suggestedAmounts: number[];
  allowCustom: boolean;
  goalCents?: number;
  raisedCents?: number;
  stripeReady?: boolean;
};

export type ModuleConfigMap = {
  hero: HeroConfig;
  agenda: AgendaConfig;
  location: LocationConfig;
  rsvp: RsvpConfig;
  potluck: PotluckConfig;
  competition: CompetitionConfig;
  junior: JuniorConfig;
  donation: DonationConfig;
};

export type EventTheme = {
  primary?: string;
  accent?: string;
  /** Joyful (default) excludes near-white/near-black. Mourning allows deep tones. */
  mode?: "joyful" | "mourning";
};

export type Event = {
  slug: string;
  title: string;
  tagline?: string;
  status: "draft" | "published";
  theme?: EventTheme;
  modules: Partial<ModuleConfigMap>;
  modulesOrder: ModuleId[];
  createdAt: number;
  updatedAt: number;
};

export type EventSummary = Pick<Event, "slug" | "title" | "tagline" | "status"> & {
  date?: string;
  banner?: ImageAsset;
};

export function summarizeEvent(event: Event): EventSummary {
  const hero = event.modules.hero;
  return {
    slug: event.slug,
    title: event.title,
    tagline: event.tagline,
    status: event.status,
    date: hero?.date,
    banner: hero?.banner,
  };
}
